import React from 'react'

export const RATE_LIMIT_DOCS_URL =
  'https://docs.github.com/en/copilot/how-tos/troubleshoot-copilot/troubleshoot-common-issues#error-sorry-your-request-was-rate-limited'

// Keys match backend rate limit error codes in CAPI exactly.
export type RateLimitCategory =
  | 'rate_limited'
  | 'user_model_rate_limited'
  | 'user_global_rate_limited'
  | 'user_weekly_rate_limited'
  | 'integration_rate_limited'

function formatRetryTime(seconds: number): string {
  if (seconds >= 3600) {
    const hours = Math.ceil(seconds / 3600)
    return `${hours} hour${hours === 1 ? '' : 's'}`
  }
  const minutes = Math.ceil(seconds / 60)
  return `${minutes} minute${minutes === 1 ? '' : 's'}`
}

// Exported for testing only
export const formatRetryTimeForTest = formatRetryTime

function UserRateLimitMessage({retryAfterSeconds}: {retryAfterSeconds: number | undefined}) {
  const timeMessage =
    retryAfterSeconds != null
      ? ` or wait ${formatRetryTime(retryAfterSeconds)} for your limit to reset`
      : ' or wait for your limit to reset'
  return (
    <>
      {`You've hit your global rate limit. Please upgrade your plan${timeMessage}. `}
      <a href={RATE_LIMIT_DOCS_URL}>Learn more</a>
    </>
  )
}

function WeeklyRateLimitMessage({retryAfterSeconds}: {retryAfterSeconds: number | undefined}) {
  const timeMessage =
    retryAfterSeconds != null ? ` Your limit will reset in ${formatRetryTime(retryAfterSeconds)}.` : ''
  return (
    <>
      {`You've exceeded your weekly usage limit.${timeMessage} Please upgrade your plan or wait for your limit to reset. `}
      <a href={RATE_LIMIT_DOCS_URL}>Learn more</a>
    </>
  )
}

export function getRateLimitMessage(
  category: string | undefined,
  retryAfterSeconds: number | undefined,
): {message: React.ReactNode; retryable: boolean} {
  switch (category) {
    case 'user_model_rate_limited':
    case 'integration_rate_limited':
      return {
        message: 'You were rate limited on the selected model. Please try again using another model.',
        retryable: true,
      }
    case 'user_global_rate_limited':
    case 'rate_limited':
      return {
        message: <UserRateLimitMessage retryAfterSeconds={retryAfterSeconds} />,
        retryable: false,
      }
    case 'user_weekly_rate_limited':
      return {
        message: <WeeklyRateLimitMessage retryAfterSeconds={retryAfterSeconds} />,
        retryable: false,
      }
    default:
      // Unknown or future categories fall back to the user-quota message
      // so the user still gets actionable guidance.
      return {
        message: <UserRateLimitMessage retryAfterSeconds={retryAfterSeconds} />,
        retryable: true,
      }
  }
}
